import { useEffect } from 'react';
import { VStack, Button, Text, Box, HStack, IconButton, Divider, useColorModeValue } from '@chakra-ui/react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import {
  MdFormatBold,
  MdFormatItalic,
  MdFormatListBulleted,
  MdFormatListNumbered,
} from 'react-icons/md';
import type { ValidationRule } from '@/lib/sdk/workflows/types';

interface TextInputTaskProps {
  description: string;
  validation: ValidationRule;
  onSubmit: (value: string) => void;
  isLoading?: boolean;
  // Controlled mode props
  controlled?: boolean;
  onValueChange?: (value: string) => void;
  initialValue?: string;
}

export function TextInputTask({
  description,
  validation,
  onSubmit,
  isLoading = false,
  controlled = false,
  onValueChange,
  initialValue = '',
}: TextInputTaskProps) {
  const borderColor = useColorModeValue('border-default', 'border-default');
  const focusBorderColor = 'brand.500';
  const toolbarActiveBg = useColorModeValue('brand.50', 'whiteAlpha.200');
  const toolbarActiveColor = useColorModeValue('brand.600', 'brand.300');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: 'Enter your response...',
      }),
    ],
    content: initialValue || '',
    onUpdate: ({ editor: e }) => {
      if (controlled && onValueChange) {
        onValueChange(e.isEmpty ? '' : e.getHTML());
      }
    },
  });

  // Sync initialValue changes (e.g. when task changes or form resets)
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      const current = editor.isEmpty ? '' : editor.getHTML();
      if (current !== (initialValue || '')) {
        editor.commands.setContent(initialValue || '', false);
      }
    }
  }, [initialValue, editor]);

  const handleSubmit = () => {
    if (!editor) return;
    const value = editor.isEmpty ? '' : editor.getHTML();

    if (validation.required && !value.trim()) {
      return;
    }
    onSubmit(value);
  };

  const isDisabled = validation.required && (editor?.isEmpty ?? true);

  return (
    <VStack spacing={4} align="stretch">
      {!controlled && (
        <Text fontSize="lg" fontWeight="medium" color="text-primary">
          {description}
        </Text>
      )}

      <Box
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        bg="bg-surface"
        overflow="hidden"
        _focusWithin={{
          borderColor: focusBorderColor,
          boxShadow: `0 0 0 1px var(--chakra-colors-brand-500)`,
        }}
        transition="border-color 0.2s, box-shadow 0.2s"
      >
        {/* Toolbar */}
        <HStack
          spacing={1}
          px={2}
          py={1}
          borderBottomWidth="1px"
          borderColor={borderColor}
          bg="bg-muted"
          flexWrap="wrap"
        >
          <IconButton
            aria-label="Bold"
            icon={<MdFormatBold />}
            size="xs"
            variant="ghost"
            isActive={editor?.isActive('bold')}
            bg={editor?.isActive('bold') ? toolbarActiveBg : undefined}
            color={editor?.isActive('bold') ? toolbarActiveColor : undefined}
            onClick={() => editor?.chain().focus().toggleBold().run()}
          />
          <IconButton
            aria-label="Italic"
            icon={<MdFormatItalic />}
            size="xs"
            variant="ghost"
            isActive={editor?.isActive('italic')}
            bg={editor?.isActive('italic') ? toolbarActiveBg : undefined}
            color={editor?.isActive('italic') ? toolbarActiveColor : undefined}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          />
          <Divider orientation="vertical" h="18px" />
          <IconButton
            aria-label="Bullet list"
            icon={<MdFormatListBulleted />}
            size="xs"
            variant="ghost"
            isActive={editor?.isActive('bulletList')}
            bg={editor?.isActive('bulletList') ? toolbarActiveBg : undefined}
            color={editor?.isActive('bulletList') ? toolbarActiveColor : undefined}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
          />
          <IconButton
            aria-label="Numbered list"
            icon={<MdFormatListNumbered />}
            size="xs"
            variant="ghost"
            isActive={editor?.isActive('orderedList')}
            bg={editor?.isActive('orderedList') ? toolbarActiveBg : undefined}
            color={editor?.isActive('orderedList') ? toolbarActiveColor : undefined}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          />
        </HStack>

        {/* Editor area */}
        <Box
          px={3}
          py={2}
          sx={{
            '.ProseMirror': {
              minHeight: '120px',
              outline: 'none',
              fontSize: 'var(--chakra-fontSizes-md)',
              color: 'var(--chakra-colors-text-primary)',
              lineHeight: 'var(--chakra-lineHeights-tall)',
            },
            '.ProseMirror p': { margin: 0, marginBottom: '0.4em' },
            '.ProseMirror p:last-child': { marginBottom: 0 },
            '.ProseMirror ul, .ProseMirror ol': { paddingLeft: '1.5em', marginBottom: '0.4em' },
            '.ProseMirror li': { marginBottom: '0.2em' },
            '.ProseMirror strong': { fontWeight: 'bold' },
            '.ProseMirror em': { fontStyle: 'italic' },
            '.ProseMirror p.is-editor-empty:first-of-type::before': {
              content: 'attr(data-placeholder)',
              float: 'left',
              color: 'var(--chakra-colors-gray-400)',
              pointerEvents: 'none',
              height: 0,
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>

      {!controlled && (
        <Button
          colorScheme="brand"
          onClick={handleSubmit}
          isLoading={isLoading}
          isDisabled={isDisabled}
        >
          Submit
        </Button>
      )}
    </VStack>
  );
}